import React, { useState, useMemo, useEffect } from "react";
// import { useIntl } from "react-intl";
import { Dropdown, Form, FormField, FormGroup, Modal, Segment } from "semantic-ui-react";

// Component
import TableSelfServiceLeaveHistory from "./TableSelfServiceLeaveHistory";
import CardLeaveRequestPanel from "./CardLeaveRequestPanel";

// Common
import DateTextBox from "react-lib/apps/common/DateTextBox";
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import PaginationShorthand from "react-lib/appcon/common/PaginationShorthand";

// Utils
import { getDefaultFilterDate } from "react-lib/utils/dateUtils";

// Serializer
import LeaveTypeSerializer from "issara-sdk/types/LeaveTypeSerializer_apps_HRM";
import LeaveRequestSerializer from "issara-sdk/types/LeaveRequestSerializer_apps_HRM";

// Table Rendering
import { LeaveRequestListType } from "./LeaveRequestListRendering";

// Interface
import {
  PickedProps,
  RunSequence,
  SetProp,
  LeaveHistoryType,
  LeaveHistoryFilterType,
  LeaveRemainingType,
  DEFAULT_LIMIT,
} from "./sequence/SelfServiceSchedule";

type CardSelfServiceLeaveHistoryProps = {
  onEvent: (e: any) => any;
  setProp: SetProp;
  // seq
  runSequence: RunSequence;
  leaveHistory?: LeaveHistoryType;
  // data
  leaveTypeList: LeaveTypeSerializer[];
  leaveRemaining: LeaveRemainingType;
  paramKey: string;
} & PickedProps;

export type ShiftTimeSaveType = {
  id: number;
  leave_start_time: string;
  leave_end_time: string;
  date: string;
};

export type ShiftTimeMapType = Record<number, ShiftTimeSaveType[]>;

const CardSelfServiceLeaveHistory = (props: CardSelfServiceLeaveHistoryProps) => {
  const [openRequestPanel, setOpenRequestPanel] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<LeaveRequestListType | null>(null);
  const [editDetail, setEditDetail] = useState<Partial<LeaveTypeSerializer>>({});
  const [filterData, setFilterData] = useState<LeaveHistoryFilterType>(() => {
    const { start_date, end_date } = getDefaultFilterDate();

    return {
      start_date,
      end_date,
    };
  });
  const [shiftTimeMap, setShiftTimeMap] = useState<ShiftTimeMapType>({});

  useEffect(() => {
    setEditDetail(props.leaveHistory?.leaveRequestDetail || {});
  }, [props.leaveHistory?.leaveRequestDetail]);

  const activePage = useMemo(() => 
    props.leaveHistory?.pagination?.activePage || 1,
  [props.leaveHistory]);

  const leaveTypeOptions = useMemo(() => {
    return (props.leaveTypeList || []).map((item: Partial<LeaveTypeSerializer>) => ({
      key: item.id,
      value: item.id,
      text: item.name,
    }));
  }, [props.leaveTypeList]);

  const leaveRequestList = useMemo(() => {
    return props.leaveHistory?.leaveRequestList || [];
  }, [props.leaveHistory]);

  const mappedFiles = useMemo(() => {
    return (
      selectedItem?.documents?.map((doc) => ({
        id: doc.id,
        file: doc.file,
      })) || []
    );
  }, [selectedItem]);

  const handleChangeDate = (name: "start_date" | "end_date") => (value: string) => {
    handleChangeData(null, { name, value });
  };

  const handleChangeData = (e: any, data: any) => {
    const { name, value, checked, type } = data;

    const newValue = typeof checked === "boolean" ? checked : value;

    setFilterData((prev) => {
      const updated = {
        ...prev,
        [name]: newValue,
      };

      return updated;
    });
  };

  const handleChangeShiftTime = (id: number, values: ShiftTimeSaveType[]) => {
    setShiftTimeMap((prev) => {
      const others = (prev[id] || []).filter(
        (s) => !values.some((v) => v.date === s.date)
      );
      return {
        ...prev,
        [id]: [...others, ...values],
      };
    });
  };

  const handleOpenRequestPanel = (id: number) => {
    const matched = props.leaveHistory?.leaveRequestList?.find((item) => item.id === id);

    setOpenRequestPanel(true);
    setSelectedItem(matched || null);

    props.runSequence({
      sequence: "SelfServiceSchedule",
      menu: "LeaveHistory",
      action: "DETAIL",
      requestId: id,
    });
  };

  const handleCloseRequestPanel = () => {
    setOpenRequestPanel(false);
    setEditDetail({});
  };

  const handleEdit = (
    form: Partial<LeaveRequestSerializer>,
    rawFiles: ({ file: File } | { id: number })[],
    id?: number
  ) => {
    if (typeof id !== "number") return;

    props.runSequence({
      sequence: "SelfServiceSchedule",
      menu: "LeaveHistory",
      action: "EDIT",
      editLeaveRequest: {
        ...form
      },
      files: rawFiles,
      requestId: id,
      filter: filterData,
      activePage,
    });

    setOpenRequestPanel(false);
  };

  const handleCancel = (id: number) => {
    props.runSequence({
      sequence: "SelfServiceSchedule",
      menu: "LeaveHistory",
      action: "CANCEL",
      requestId: id,
      filter: filterData,
      activePage,
    });
  };

  const handleSearch = () => {
    props.runSequence({
      sequence: "SelfServiceSchedule",
      menu: "LeaveHistory",
      action: "FETCH",
      filter: filterData,
      activePage,
    });
  };

  const handlePageChange = (activePage: number) => {
    props.runSequence({
      sequence: "SelfServiceSchedule",
      menu: "LeaveHistory",
      action: "FETCH",
      filter: filterData,
      activePage,
    });
  };

  return (
    <div>
      {/* Edit Panel */}
      <Modal open={openRequestPanel} onClose={handleCloseRequestPanel}>
        <Segment id="CardSelfServiceLeaveHistory-Segment-Title" inverted color="yellow">
          Edit Leave Request
        </Segment>
        <CardLeaveRequestPanel
          mode="edit"
          initValue={editDetail}
          existFiles={mappedFiles}
          onSubmit={handleEdit}
          onCancel={handleCloseRequestPanel}
          runSequence={props.runSequence}
          setProp={props.setProp}
          buttonLoadCheck={props.buttonLoadCheck}
          leaveTypeList={props.leaveTypeList}
          leaveRemaining={props.leaveRemaining}
          paramKey={props.paramKey}
        />
      </Modal>

      {/* Filter Section */}
      <Form>
        <FormGroup inline>
          <FormField inline>
            <label>Date</label>
          </FormField>
          <FormField inline>
            <DateTextBox
              id="CardSelfServiceLeaveHistory-DateTextBox-StartDate"
              // useAdDate={true}
              value={filterData?.start_date || ""}
              onChange={handleChangeDate("start_date")}
            />
          </FormField>
          <FormField inline>
            <DateTextBox
              id="CardSelfServiceLeaveHistory-DateTextBox-EndDate"
              // useAdDate={true}
              value={filterData?.end_date || ""}
              onChange={handleChangeDate("end_date")}
            />
          </FormField>
          <FormField inline>
            <label>Leave Type</label>
          </FormField>
          <FormField inline width={2}>
            <div style={{ width: "100%" }}>
              <Dropdown
                id="CardSelfServiceLeaveHistory-Dropdown-LeaveType"
                selection
                fluid
                clearable
                options={leaveTypeOptions}
                value={filterData?.leave_type || undefined}
                name="leave_type"
                onChange={handleChangeData}
              />
            </div>
          </FormField>
          <FormField inline>
            <ButtonLoadCheck
              id="CardSelfServiceLeaveHistory-ButtonLoadCheck-Search"
              // func
              setProp={props.setProp}
              onClick={handleSearch}
              // data
              paramKey={`${props.paramKey}_FETCH`}
              buttonLoadCheck={props.buttonLoadCheck?.[`${props.paramKey}_FETCH`]}
              title="Search"
              color="blue"
              name="FETCH"
              size="medium"
            />
          </FormField>
        </FormGroup>
      </Form>

      {/* Table Section */}
      <div>
        <TableSelfServiceLeaveHistory
          // seq
          setProp={props.setProp}
          // func
          onOpenRequest={handleOpenRequestPanel}
          onCancel={handleCancel}
          onChangeShiftTime={handleChangeShiftTime}
          // data
          leaveRequestList={leaveRequestList}
          leaveTypeList={props.leaveTypeList}
          shiftTimeMap={shiftTimeMap}
          // options
          paramKey={props.paramKey}
        />
      </div>

      {/* Pagination */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginTop: "0.75rem",
        }}
      >
        <PaginationShorthand
          activePage={props.leaveHistory?.pagination?.activePage || 0}
          limit={DEFAULT_LIMIT}
          total={props.leaveHistory?.pagination?.total}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
};

export default CardSelfServiceLeaveHistory;
